import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { onActivateKey } from './activate';
import { cx } from './classNames';
import { FieldLabel } from './FieldLabel';
import { ListPopup } from './ListPopup';
import { useOutsideDismiss } from './usePopupPosition';
import { useDropDownItems, type LoadItemsRequest, type LoadItemsResult } from './useDropDownItems';

/** Pendant zu `SfDropDownList`. */
export interface DropDownListProps<TValue, TItem> {
  value: TValue | null;
  onChange: (value: TValue | null, item: TItem | null) => void;
  items?: TItem[];
  /** Serverseitige Quelle mit Suche und Nachladen. */
  loadItems?: (request: LoadItemsRequest) => Promise<LoadItemsResult<TItem>>;
  fields: { value: keyof TItem & string; text: keyof TItem & string };
  itemTemplate?: (item: TItem) => ReactNode;
  /** Lädt den aktuellen Wert nach, wenn er nicht in der Liste steht. */
  resolveValue?: (value: TValue) => Promise<TItem | null>;
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Nur für echte Beispielwerte — nicht als Ersatz für `label` (REQ-24 §3.2). */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  allowFiltering?: boolean;
  showClearButton?: boolean;
  /** Standard 10 — entspricht `Query().Take(10)`. */
  pageSize?: number;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function DropDownList<TValue, TItem>(props: DropDownListProps<TValue, TItem>) {
  const { fields, onChange, resolveValue, value } = props;
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [resolved, setResolved] = useState<TItem | null>(null);
  const anchorRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutsideDismiss(open, close, [anchorRef, popupRef]);

  const { visibleItems, filter, setFilter, loadMore } = useDropDownItems<TItem>({
    open,
    items: props.items,
    loadItems: props.loadItems,
    textField: fields.text,
    pageSize: props.pageSize ?? 10,
  });

  // Der Wert ist fremd typisiert; die Zuordnung erfolgt wie im Altclient über
  // das in `fields.value` benannte Feld.
  const valueOf = (item: TItem) => item[fields.value] as unknown as TValue;
  const textOf = (item: TItem) => String(item[fields.text] ?? '');

  const listed = visibleItems.find((item) => valueOf(item) === value) ?? null;
  const selected = listed ?? (resolved !== null && valueOf(resolved) === value ? resolved : null);

  useEffect(() => {
    if (value === null || resolveValue === undefined || listed !== null) return;
    let active = true;
    void resolveValue(value).then((item) => {
      if (active) setResolved(item);
    });
    return () => {
      active = false;
    };
  }, [listed, resolveValue, value]);

  /**
   * Schließt die Liste und holt den Fokus aus dem Popup zurück ins Feld. Das Suchfeld
   * verschwindet mit dem Popup, ein Klick auf einen Eintrag setzt den Fokus auf `body`. Ohne
   * Rückgabe erführe eine Grid-Zelle weder Enter noch Fokusverlust und speicherte die
   * Auswahl nie.
   */
  const closeList = () => {
    const active = document.activeElement;
    if (
      active === null ||
      active === document.body ||
      popupRef.current?.contains(active) === true
    ) {
      inputRef.current?.focus();
    }
    setOpen(false);
  };

  const commit = (item: TItem | null) => {
    closeList();
    setResolved(item);
    onChange(item === null ? null : valueOf(item), item);
  };

  const clear = () => {
    commit(null);
  };

  const toggle = () => {
    if (props.disabled === true || props.readOnly === true) return;
    setOpen((current) => !current);
    setFocusedIndex(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        toggle();
        return;
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const length = Math.max(visibleItems.length, 1);
      setFocusedIndex((index) => (index + delta + length) % length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!open) {
        toggle();
        return;
      }
      const item = visibleItems[focusedIndex];
      if (item !== undefined) commit(item);
    } else if (event.key === 'Escape') {
      closeList();
    }
  };

  /**
   * Das Suchfeld liegt im Portal, im React-Baum aber unter dem Feld. Tastendrücke dort würden
   * sonst bis zur umgebenden Grid-Zelle durchgereicht: Enter speicherte den alten Wert,
   * Escape bräche die Bearbeitung ab.
   */
  const onFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) return;
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      // Wie bei `SfDropDownList` übernimmt Enter nach dem Filtern den ersten Treffer.
      const item = visibleItems[focusedIndex] ?? visibleItems[0];
      if (item !== undefined) commit(item);
      return;
    }
    onKeyDown(event);
  };

  const text = selected === null ? '' : textOf(selected);

  return (
    <>
      <FieldLabel
        id={props.id}
        elementRef={anchorRef}
        className={props.className}
        style={props.style}
        title={props.title}
        groupClass="e-ddl"
        label={props.label}
        hint={props.hint}
        focused={open}
        disabled={props.disabled}
        onKeyDown={onKeyDown}
      >
        {({ id }) => (
          <>
            <input
              ref={inputRef}
              id={id}
              type="text"
              className="e-input"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              value={text}
              placeholder={props.placeholder}
              readOnly
              disabled={props.disabled === true}
              onClick={toggle}
              onChange={() => {
                /* nur Anzeige — die Auswahl erfolgt über die Liste */
              }}
            />
            {props.showClearButton === true ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Auswahl löschen"
                className={cx(
                  'e-clear-icon',
                  'e-icons',
                  'fa-solid fa-xmark',
                  text === '' && 'e-clear-icon-hide',
                )}
                onClick={clear}
                onKeyDown={onActivateKey(clear)}
              />
            ) : null}
            <span
              role="button"
              tabIndex={0}
              aria-label="Liste öffnen"
              className="e-input-group-icon e-ddl-icon e-icons fa-solid fa-caret-down"
              onClick={toggle}
              onKeyDown={onActivateKey(toggle)}
            />
          </>
        )}
      </FieldLabel>
      <ListPopup<TItem>
        open={open}
        anchorRef={anchorRef}
        popupRef={popupRef}
        items={visibleItems}
        getKey={(item, index) => `${String(valueOf(item))}-${String(index)}`}
        getText={textOf}
        isActive={(item) => valueOf(item) === value}
        focusedIndex={focusedIndex}
        onFocusIndex={setFocusedIndex}
        onSelect={commit}
        itemTemplate={props.itemTemplate}
        allowFiltering={props.allowFiltering}
        filterText={filter}
        onFilterChange={(text) => {
          setFilter(text);
          setFocusedIndex(-1);
        }}
        onFilterKeyDown={onFilterKeyDown}
        onScrollEnd={loadMore}
      />
    </>
  );
}

export default DropDownList;
